/**
 * Propose a task: title, type, difficulty, suggested priority, prerequisite
 * tasks, and a Markdown description with edit and preview.
 *
 * The type choices come from the profile's `visibleTaskTypes` — the server's
 * own answer to "what may this person propose" — so an annotator's form has
 * one type and no gap where another would be. Validation renders as one
 * error summary plus field-level messages, and a failed submission keeps
 * everything typed.
 *
 * The prerequisite picker reads *current* server data every time the form
 * opens — including the in-page "propose another" reopening, which bumps
 * `formGeneration` and refetches. The old form loaded the list once per
 * mount, so a task proposed seconds earlier was missing from the very next
 * proposal's dependencies; and it silently carried the previous proposal's
 * selections and settings into the new one. Every reopening now starts
 * clean: fields reset to their defaults, selections reset to none, and the
 * candidate list is whatever the server says right now.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiFailure, fetchTasks, newIdempotencyKey, proposeTask } from '../api.js';
import {
  DependencyPicker,
  MarkdownField,
  useWorkspaceCopy,
  type DependencyCandidates,
} from '../components.js';
import { useSession } from '../session.js';
import { Button, ChoiceGroup, InlineAlert, TextField, useToasts } from '../ui.js';
import { PageTitle } from './common.js';

export function TaskNewPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const { announce } = useToasts();

  const visibleTypes =
    session.status === 'authenticated' ? session.profile.visibleTaskTypes : [];
  const defaultType = visibleTypes.length === 1 ? (visibleTypes[0] ?? null) : null;

  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState<string | null>(defaultType);
  const [difficulty, setDifficulty] = useState<string | null>('MEDIUM');
  const [suggestedPriority, setSuggestedPriority] = useState<string | null>('P3');
  const [description, setDescription] = useState('');
  const [prerequisiteIds, setPrerequisiteIds] = useState<readonly string[]>([]);
  const [candidates, setCandidates] = useState<DependencyCandidates>({
    status: 'loading',
  });
  // Counts form *openings*, not renders: the initial mount is generation 0,
  // and each "propose another" is the next one. The candidate fetch keys on
  // it, so every opening reads the tasks that exist at that moment.
  const [formGeneration, setFormGeneration] = useState(0);

  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  // One key per proposal *intent*: a retry after failure reuses it, so the
  // server can replay rather than duplicate. A fresh form gets a fresh key.
  const [intentKey, setIntentKey] = useState(() => newIdempotencyKey());

  const loadCandidates = useCallback(() => {
    setCandidates({ status: 'loading' });
    let cancelled = false;
    fetchTasks()
      .then((listing) => {
        if (!cancelled) setCandidates({ status: 'ready', tasks: listing.tasks });
      })
      .catch(() => {
        // The picker is optional — the proposal still works — but a failure
        // says so and offers a retry instead of impersonating emptiness.
        if (!cancelled) setCandidates({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadCandidates(), [loadCandidates, formGeneration]);

  const errors = useMemo(() => {
    const found: { field: string; message: string }[] = [];
    if (title.trim().length === 0)
      found.push({ field: 'title', message: copy.propose.titleMissing });
    if (description.trim().length === 0) {
      found.push({ field: 'description', message: copy.propose.descriptionMissing });
    }
    return found;
  }, [title, description, copy]);

  const submit = useCallback(() => {
    setSubmitted(true);
    setFailure(null);
    if (
      errors.length > 0 ||
      taskType === null ||
      difficulty === null ||
      suggestedPriority === null
    ) {
      return;
    }
    setBusy(true);
    proposeTask(intentKey, {
      title: title.trim(),
      taskType,
      difficulty,
      suggestedPriority,
      description,
      ...(prerequisiteIds.length > 0 ? { prerequisiteTaskIds: prerequisiteIds } : {}),
    })
      .then((result) => {
        setBusy(false);
        setCreated(result.taskId);
        announce(copy.toasts.proposed);
      })
      .catch((thrown: unknown) => {
        setBusy(false);
        setFailure(thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'));
      });
  }, [
    errors,
    taskType,
    difficulty,
    suggestedPriority,
    title,
    description,
    prerequisiteIds,
    intentKey,
    announce,
    copy,
  ]);

  if (created !== null) {
    return (
      <>
        <PageTitle>{copy.propose.successTitle}</PageTitle>
        <InlineAlert tone="success" role="status">
          {copy.propose.successBody}
        </InlineAlert>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link
            to={`/tasks/${created}`}
            style={{ color: 'var(--text-link)', font: 'var(--type-body-md)' }}
          >
            {copy.propose.viewTask}
          </Link>
          <Link
            to="/tasks"
            style={{ color: 'var(--text-link)', font: 'var(--type-body-md)' }}
          >
            {copy.task.backToBoard}
          </Link>
        </div>
        <div style={{ marginTop: 'var(--space-lg)' }}>
          <Button
            variant="outline"
            onClick={() => {
              // A new proposal is a new form: every field back to its
              // default, no selections carried over, a fresh idempotency
              // key, and — through formGeneration — a fresh candidate list
              // that includes the task created a moment ago.
              setCreated(null);
              setTitle('');
              setDescription('');
              setTaskType(defaultType);
              setDifficulty('MEDIUM');
              setSuggestedPriority('P3');
              setPrerequisiteIds([]);
              setSubmitted(false);
              setFailure(null);
              setIntentKey(newIdempotencyKey());
              setFormGeneration((generation) => generation + 1);
            }}
          >
            {copy.propose.title}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle>{copy.propose.title}</PageTitle>

      {submitted && errors.length > 0 ? (
        <InlineAlert tone="error" title={copy.propose.errorSummary} role="alert">
          <ul style={{ margin: 0, paddingInlineStart: 20 }}>
            {errors.map((error) => (
              <li key={error.field}>{error.message}</li>
            ))}
          </ul>
        </InlineAlert>
      ) : null}

      {failure !== null ? (
        <InlineAlert
          tone={failure.kind === 'offline' ? 'warning' : 'error'}
          title={
            failure.kind === 'offline'
              ? copy.states.offlineTitle
              : copy.states.saveFailedTitle
          }
          role="alert"
        >
          {failure.kind === 'offline'
            ? copy.states.offlineBody
            : (failure.detail ?? copy.states.saveFailedBody)}
        </InlineAlert>
      ) : null}

      <form
        style={{ maxWidth: 720 }}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={copy.propose.titleLabel}
          hint={copy.propose.titleHint}
          value={title}
          onChange={setTitle}
          maxLength={200}
          error={
            submitted && title.trim().length === 0 ? copy.propose.titleMissing : null
          }
        />

        <ChoiceGroup
          legend={copy.propose.typeLabel}
          value={taskType}
          onChange={setTaskType}
          inline
          choices={visibleTypes.map((value) => ({
            value,
            label: copy.types[value] ?? value,
            definition: copy.propose.typeDefinitions[value],
          }))}
        />

        <ChoiceGroup
          legend={copy.propose.difficultyLabel}
          value={difficulty}
          onChange={setDifficulty}
          inline
          choices={['EXTRA_SMALL', 'SMALL', 'MEDIUM', 'LARGE', 'EXTRA_LARGE'].map(
            (value) => ({
              value,
              label: copy.difficulties[value] ?? value,
              definition: copy.propose.difficultyDefinitions[value],
            }),
          )}
        />

        <ChoiceGroup
          legend={copy.propose.suggestedPriorityLabel}
          hint={copy.propose.suggestedPriorityHint}
          value={suggestedPriority}
          onChange={setSuggestedPriority}
          inline
          choices={['P1', 'P2', 'P3', 'P4'].map((value) => ({
            value,
            label: copy.priorities[value] ?? value,
          }))}
        />

        <DependencyPicker
          legend={copy.propose.prerequisitesLabel}
          hint={copy.propose.prerequisitesHint}
          candidates={candidates}
          selected={prerequisiteIds}
          onToggle={(taskId, wanted) => {
            setPrerequisiteIds((current) =>
              wanted
                ? current.includes(taskId)
                  ? current
                  : [...current, taskId]
                : current.filter((id) => id !== taskId),
            );
          }}
          onRetry={loadCandidates}
        />

        <MarkdownField
          label={copy.propose.descriptionLabel}
          hint={copy.propose.descriptionHint}
          value={description}
          onChange={setDescription}
          rows={12}
          maxLength={20000}
          error={
            submitted && description.trim().length === 0
              ? copy.propose.descriptionMissing
              : null
          }
        />

        <Button variant="primary" type="submit" busy={busy}>
          {busy ? copy.propose.submitting : copy.propose.submit}
        </Button>
      </form>
    </>
  );
}
